import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { fetchStatisticsData } from '../../features/Statistics/Statistics';
import Loading from '../Loading';
import CountUp from 'react-countup';

// Utility: convert Arabic-Indic digits to European digits
function replaceArabicIndicDigits(str) {
  if (typeof str !== 'string') return str;
  // Arabic-Indic (U+0660 - U+0669) and Extended Arabic-Indic (U+06F0 - U+06F9)
  const map = {
    '\u0660': '0','\u0661':'1','\u0662':'2','\u0663':'3','\u0664':'4','\u0665':'5','\u0666':'6','\u0667':'7','\u0668':'8','\u0669':'9',
    '\u06F0':'0','\u06F1':'1','\u06F2':'2','\u06F3':'3','\u06F4':'4','\u06F5':'5','\u06F6':'6','\u06F7':'7','\u06F8':'8','\u06F9':'9'
  };
  return str.replace(/[٠-٩۰-۹]/g, (d) => map[d] ?? d);
}

// Robust number parser that handles common formats and localized digits
function parseNumber(value) {
  if (value == null) return 0; // null or undefined
  if (typeof value === 'number') return value;

  let s = String(value).trim();
  if (s === '') return 0;

  // convert localized digits (Arabic-Indic)
  s = replaceArabicIndicDigits(s);

  // remove non-breaking spaces and common whitespace
  s = s.replace(/\u00A0/g, '').replace(/\s+/g, '');

  // If both dot and comma exist, assume dot is thousands sep and comma is decimal (e.g. "1.234,56")
  if (s.includes('.') && s.includes(',')) {
    s = s.replace(/\./g, '').replace(/,/g, '.');
  } else {
    // remove commas used as thousands separators (e.g. "1,234")
    s = s.replace(/,/g, '');
  }

  // Remove any non-digit except minus sign and dot
  s = s.replace(/[^0-9.-]/g, '');

  const num = Number(s);
  return Number.isFinite(num) ? num : 0;
}

export default function ShowStatistics() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { statistics = [], loading } = useSelector((state) => state.statistics || {});
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const isRTL = i18n.dir() === 'rtl';

  useEffect(() => {
    dispatch(fetchStatisticsData({ perPage: 3000, year: selectedYear }));
  }, [dispatch, i18n.language, selectedYear]);

  if (loading) return <Loading />;

  return (
    <div className={`max-w-7xl mx-auto mb-10 p-4 sm:p-6 bg-white rounded-xl shadow-lg mt-6 ${isRTL ? 'rtl' : 'ltr'}`}>
      {/* Header Section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div className='w-full'>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">{t('zadStatistics')}</h1>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          <div className="flex items-center gap-3 flex-col sm:flex-row w-full sm:w-auto">
            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">{t('selectYear')}:</label>
            <div className="relative w-full sm:w-40">
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                className="px-4 py-2 w-full border outline-none border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
              >
                {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>

              <div className="pointer-events-none absolute inset-y-0 end-0 flex items-center pe-2 text-gray-700">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Grid with animated numbers */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {statistics.map((stat, index) => {
          const numericValue = parseNumber(stat?.count);
          const dateText = stat?.date ?? '';

          return (
            <div key={stat?.id ?? index} className="bg-white p-6 rounded-xl border border-gray-200 hover:shadow-md transition-shadow duration-300">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-medium text-gray-900 truncate" title={stat?.name}>{stat?.name}</h3>
                <span className="bg-teal-100 text-teal-800 text-xs font-medium px-2.5 py-1 rounded-full">#{index + 1}</span>
              </div>

              <div className="flex items-end justify-between mt-6">
                <div>
                  {/* Animated number with CountUp. key ensures animation restarts if value changes. */}
                  <p className="text-3xl font-bold text-[#07A024]" aria-live="polite">
                    <CountUp
                      start={0}
                      end={numericValue}
                      duration={1.5}
                      separator="," 
                      decimals={Number.isInteger(numericValue) ? 0 : 2}
                      preserveValue={false}
                      key={numericValue}
                    />
                  </p>
                  <p className="text-sm text-gray-500 mt-2">{stat?.subTitle ?? ''}</p>
                </div>

                <div className="text-right">
                  <p className="text-sm text-gray-500">{dateText}</p>
                  <p className="text-xs text-gray-400 mt-1">{t('date')}</p>
                </div>
              </div>

            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="pt-6 border-t border-gray-200 flex flex-col sm:flex-row justify-between items-center">
        <div className="flex items-center justify-center gap-2 text-gray-600">
          <span>{t('latestUpdate')}:</span>
          <span className="font-medium text-emerald-600">{statistics[0]?.last_update?.split('T')?.[0] ?? '-'}</span>
        </div>
      </div>
    </div>
  );
}
